import React, { useEffect, useMemo, useRef, useState } from 'react';
import { removerCaracteresNaoFiltraveis } from 'utils/formatUtils';
import { IOptions } from 'utils/types';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  TextField,
  Tooltip,
} from '@mui/material';

import {
  PlaceholderText,
  SelectAllBox,
  TruncatedInputLabel,
  WrapperPaperList,
} from './CustomAutocompleteMultiple.styles';

export interface ICustomAutocompleteMultipleProps
  extends Omit<
    AutocompleteProps<IOptions, true, any, false, any>,
    'onChange' | 'renderInput'
  > {
  label: string;
  name: string;
  setValue: (value: IOptions[]) => void;
  checkboxId?: string;
  onChange?: (
    event: React.SyntheticEvent,
    value: IOptions[],
    reason: string,
    details?: AutocompleteChangeDetails<IOptions>,
  ) => void;
}

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

export default function CustomAutocompleteMultiple(
  props: ICustomAutocompleteMultipleProps,
) {
  const { setValue, onChange, options, ...autocompleteProps } = props;
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');

  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [props.label]);

  const filteredOptions = useMemo(() => {
    if (inputValue === '') {
      return options;
    }
    return options.filter((option) =>
      removerCaracteresNaoFiltraveis(option.label)
        .toLowerCase()
        .includes(removerCaracteresNaoFiltraveis(inputValue.toLowerCase())),
    );
  }, [inputValue, options]);

  useEffect(() => {
    setSelectAll(props.value?.length === options.length);
  }, [props.value?.length, options.length]);

  const handleSelectAll = () => {
    if (!selectAll) {
      setValue([...filteredOptions]);
    } else {
      setValue([]);
    }
    setSelectAll(!selectAll);
  };

  const handleChange = (
    event: React.SyntheticEvent,
    value: readonly IOptions[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<IOptions>,
  ) => {
    if (reason === 'clear' || reason === 'removeOption') {
      setSelectAll(false);
    }
    setValue([...value]);
    if (onChange) {
      onChange(event, [...value], reason, details);
    }
  };

  return (
    <FormControl fullWidth variant="filled">
      <Autocomplete
        {...autocompleteProps}
        data-testid="custom-autocomplete-multiple"
        options={filteredOptions}
        size="small"
        limitTags={1}
        getOptionLabel={(option) => option.label}
        filterOptions={(options) => options} // disable default filtering
        renderInput={(params) => (
          <>
            <Tooltip
              title={isTruncated ? props.label : ''}
              arrow
              disableHoverListener={!isTruncated}
              placement="top"
            >
              <TruncatedInputLabel
                ref={spanRef}
                htmlFor={params.id}
                data-testid={`label-${props.name}`}
              >
                {props.label}
              </TruncatedInputLabel>
            </Tooltip>
            <TextField
              {...params}
              variant="filled"
              hiddenLabel
              data-testid={`multiple-autocomplete-search-${props.name}`}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {(() => {
                        const value = props.value || [];
                        if (value.length > 1 && value.length < options.length) {
                          return (
                            <PlaceholderText>{`${value.length} opções selecionadas`}</PlaceholderText>
                          );
                        }
                        if (
                          value.length === 0 ||
                          value.length === options.length
                        ) {
                          return <PlaceholderText>Todas</PlaceholderText>;
                        }
                        if (value.length === 1) {
                          return (
                            <PlaceholderText>{value[0].label}</PlaceholderText>
                          );
                        }
                      })()}
                    </>
                  ),
                },
              }}
            />
          </>
        )}
        renderOption={(params, option, { selected }) => {
          const { key, ...optionParams } = { ...params, key: option.value };
          return (
            <li {...optionParams} key={key}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.label}
            </li>
          );
        }}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onKeyDownCapture={(e) => {
          if (
            (e.key === 'Backspace' || e.key === 'Delete') &&
            inputValue === ''
          ) {
            e.stopPropagation();
          }
        }}
        onChange={handleChange}
        noOptionsText="Nenhum dado encontrado."
        PaperComponent={(paperProps) => {
          const { children, ...restPaperProps } = paperProps;
          return (
            <WrapperPaperList {...restPaperProps}>
              <SelectAllBox onMouseDown={(e) => e.preventDefault()}>
                <FormControlLabel
                  label="Selecionar tudo"
                  htmlFor={
                    props.checkboxId ??
                    `select-all-checkbox-for-autocomplete-${props.id}`
                  }
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    handleSelectAll();
                  }}
                  control={
                    <Checkbox
                      id={
                        props.checkboxId ??
                        `select-all-checkbox-for-autocomplete-${props.id}`
                      }
                      name={
                        props.checkboxId ??
                        `select-all-checkbox-for-autocomplete-${props.id}`
                      }
                      sx={{ marginRight: '8px' }}
                      size="small"
                      checked={selectAll}
                    />
                  }
                />
              </SelectAllBox>
              <Divider />
              {children}
            </WrapperPaperList>
          );
        }}
      />
    </FormControl>
  );
}
